package com.patronat.top.features.usermanagement.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import com.patronat.top.core.domain.models.profiles.User
import com.patronat.top.dto.UserStatus
import com.patronat.top.ui.molecules.menu.ActionMenuItem
import com.patronat.top.ui.molecules.pagination.TablePagination
import com.patronat.top.ui.molecules.status.BadgeStyle
import com.patronat.top.ui.molecules.status.StatusBadge
import com.patronat.top.ui.organisms.table.AppDataTable
import com.patronat.top.ui.organisms.table.TableColumn
import com.patronat.top.ui.theme.*
import kotlinx.datetime.Instant
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import top.features.user_management.generated.resources.*

@Composable
fun UsersTable(
    users: List<User>,
    totalCount: Long,
    currentPage: Int,
    pageSize: Int,
    expandedUserMenu: User?,
    onPageChange: (Int) -> Unit,
    onPageSizeChange: (Int) -> Unit,
    onActionClick: (User) -> Unit,
    onDismissMenu: () -> Unit,
    onEditProfile: (User) -> Unit,
    onBlockUser: (User) -> Unit,
    onUnblockUser: (User) -> Unit,
    isUsersScreen: Boolean,
    modifier: Modifier = Modifier
) {
    val titleName = stringResource(Res.string.table_col_name)
    val titlePhone = stringResource(Res.string.table_col_phone)
    val titleEmail = stringResource(Res.string.table_col_email)
    val titleStatus = stringResource(Res.string.table_col_status)
    val titleRequests = stringResource(Res.string.table_col_requests)
    val titleDate = stringResource(Res.string.table_col_date)
    val titleActions = stringResource(Res.string.table_col_actions)

    val placeholder = stringResource(Res.string.placeholder_dash)
    val actionDesc = stringResource(Res.string.cd_actions_menu)

    val columns =
        listOf(
            TableColumn<User>(title = titleName, widthWeight = 1.6f, alignment = Alignment.Start) { user ->
                Text(text = "${user.firstName} ${user.lastName}", style = LabelLarge, maxLines = 1)
            },
            TableColumn<User>(title = titlePhone, widthWeight = 1.3f) { user ->
                Text(user.phone, style = BodyMedium, maxLines = 1)
            },
            TableColumn<User>(title = titleEmail, widthWeight = 1.5f) { user ->
                Text(text = user.email ?: placeholder, style = BodyMedium, maxLines = 1)
            },
            TableColumn<User>(title = titleStatus, widthWeight = 1.4f, alignment = Alignment.CenterHorizontally) { user ->
                val statusText = stringResource(getStatusResource(user.status))
                StatusBadge(text = statusText, style = user.status.badgeStyle)
            },
            TableColumn<User>(title = titleRequests, widthWeight = 0.8f, alignment = Alignment.CenterHorizontally) {
                Text("0", style = LabelLarge)
            },
            TableColumn<User>(title = titleDate, widthWeight = 1.0f, alignment = Alignment.CenterHorizontally) { user ->
                Text(text = user.createdAt.toDisplayString(placeholder), style = LabelLarge)
            },
            TableColumn<User>(title = titleActions, widthWeight = 0.5f, alignment = Alignment.CenterHorizontally) { user ->
                Box(modifier = Modifier.wrapContentWidth()) {
                    IconButton(onClick = { onActionClick(user) }) {
                        Icon(painter = AppIcons.Action3Dot, contentDescription = actionDesc, tint = MineShaft500)
                    }
                    if (expandedUserMenu == user) {
                        Popup(
                            onDismissRequest = onDismissMenu,
                            alignment = Alignment.BottomEnd
                        ) {
                            Column(
                                modifier = Modifier
                                    .width(224.dp)
                                    .background(White, RoundedCornerShape(12.dp))
                                    .border(0.75.dp, DropdownBorder, RoundedCornerShape(12.dp))
                                    .padding(5.dp)
                            ) {
                                if (isUsersScreen) {
                                    if (user.status == UserStatus.BLOCKED) {
                                        ActionMenuItem(
                                            text = stringResource(Res.string.action_unblock_user),
                                            icon = AppIcons.Unblock,
                                            onClick = { onUnblockUser(user) }
                                        )
                                    } else {
                                        ActionMenuItem(
                                            text = stringResource(Res.string.action_block_user),
                                            icon = AppIcons.Block,
                                            onClick = { onBlockUser(user) },
                                            textColor = AccentPink,
                                        )
                                    }
                                } else {
                                    ActionMenuItem(
                                        text = stringResource(Res.string.action_edit_profile),
                                        icon = AppIcons.Edit,
                                        onClick = { onEditProfile(user) }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        )

    Column(modifier = modifier.fillMaxSize()) {
        AppDataTable(
            columns = columns,
            data = users,
            modifier = Modifier.weight(1f)
        )

        TablePagination(
            currentPage = currentPage,
            totalItems = totalCount.toInt(),
            pageSize = pageSize,
            onPageChange = onPageChange,
            onPageSizeChange = onPageSizeChange
        )
    }
}

private fun getStatusResource(status: UserStatus): StringResource {
    return when (status) {
        UserStatus.UNAUTHORIZED -> Res.string.status_unauthorized
        UserStatus.ACTIVE -> Res.string.status_active
        UserStatus.DEACTIVATED -> Res.string.status_deactivated
        UserStatus.BLOCKED -> Res.string.status_blocked
        else -> Res.string.placeholder_dash
    }
}

private val UserStatus.badgeStyle: BadgeStyle
    get() = when (this) {
        UserStatus.UNAUTHORIZED -> BadgeStyle.WARNING
        UserStatus.ACTIVE -> BadgeStyle.ACTIVE
        UserStatus.DEACTIVATED -> BadgeStyle.INACTIVE
        UserStatus.BLOCKED -> BadgeStyle.ERROR
    }

private fun Instant?.toDisplayString(placeholder: String): String {
    if (this == null) return placeholder
    return try {
        this.toString().take(10).split("-").reversed().joinToString(".")
    } catch (e: Exception) {
        this.toString()
    }
}